import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import GradientIcon from '@/components/common/GradientIcon';
import { adFormats, advertiseSteps, demoCampaigns, demoDaily, partnerApps, type AdFormatId } from '@/mocks/advertise';
import AdMockup from './AdMockup';
import LiveDemo from './LiveDemo';

const REGISTER = '/sponsor/register';

function Hero({ onDemo }: { onDemo: () => void }) {
  return (
    <section className="bg-gradient-to-b from-[#e9ecff] via-[#f4f2ff] to-white pt-28 pb-14">
      <div className="max-w-6xl mx-auto px-4 md:px-6 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/80 border border-[#dfe3ff] px-3 py-1 text-xs font-semibold text-[#2f51d6]">
            <i className="ri-megaphone-fill" /> For advertisers
          </span>
          <h1 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tight text-[#1f2329] leading-[1.1]">
            Show your ads inside the apps your customers already use
          </h1>
          <p className="mt-4 text-[#646a73] max-w-xl">
            Register once, choose an ad style, and pick partner apps like the Survey app and IDI app. Your dashboard shows every view and click.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to={REGISTER} className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#2f51d6] text-white text-sm font-semibold hover:brightness-110">
              Register as advertiser <i className="ri-arrow-right-line" />
            </Link>
            <button type="button" onClick={onDemo} className="inline-flex items-center gap-2 h-11 px-6 rounded-full border border-[#2f51d6] text-[#2f51d6] bg-white text-sm font-semibold hover:bg-[#f5f7ff] cursor-pointer">
              <i className="ri-play-circle-line" /> Try the live demo
            </button>
          </div>
        </div>

        {/* Mini preview of the advertiser dashboard */}
        <div className="rounded-3xl bg-white/80 backdrop-blur border border-white shadow-[0_30px_70px_-35px_rgba(47,81,214,0.6)] p-4">
          <div className="rounded-2xl border border-[#eef0f3] bg-white p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-[#1f2329]">Year-end promo</span>
              <span className="text-[10px] font-semibold rounded-full bg-[#e9faef] text-[#0e9f7e] px-2 py-0.5">Running</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {[
                { k: 'Views', v: '48,210' },
                { k: 'Clicks', v: '1,302' },
                { k: 'Click rate', v: '2.7%' },
              ].map((s) => (
                <div key={s.k} className="rounded-xl bg-[#f7f8fa] px-3 py-2">
                  <div className="text-[10px] text-[#8f959e]">{s.k}</div>
                  <div className="text-lg font-extrabold text-[#1f2329] tabular-nums">{s.v}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-end gap-1.5 h-20">
              {demoDaily.map((d) => (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                  <span className="w-full rounded-t bg-gradient-to-t from-[#2f51d6] to-[#8fa3ff]" style={{ height: `${(d.views / 16000) * 64}px` }} />
                  <span className="text-[9px] text-[#a3a8b0]">{d.day}</span>
                </div>
              ))}
            </div>
            <p className="mt-2 text-[10px] text-[#a3a8b0]">Example data</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Steps() {
  return (
    <section className="bg-white py-14">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <h2 className="text-center text-2xl md:text-[30px] font-bold text-[#1f2329] tracking-tight">How advertising works</h2>
        <ol className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {advertiseSteps.map((s, i) => (
            <li key={s.title} className="relative rounded-2xl bg-[#f7f8fa] p-5">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center">
                  <GradientIcon icon={s.icon} tone={(['blue', 'violet', 'pink', 'teal'] as const)[i]} size="text-xl" />
                </span>
                <span className="text-xs font-bold text-[#a3a8b0]">Step {i + 1}</span>
              </div>
              <h3 className="mt-3 font-bold text-[#1f2329]">{s.title}</h3>
              <p className="mt-1 text-sm text-[#646a73]">{s.text}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Formats({ onDemo }: { onDemo: (id: AdFormatId) => void }) {
  return (
    <section id="formats" className="scroll-mt-16 bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-4 pb-6 text-center">
        <h2 className="text-2xl md:text-[30px] font-bold text-[#1f2329] tracking-tight">Choose your ad style</h2>
        <p className="mt-2 text-sm text-[#646a73]">Six formats on our partner apps. Open a live demo to see each one in action.</p>
      </div>
      {adFormats.map((f, i) => (
        <div key={f.id} className={i % 2 === 0 ? 'bg-[#f2f3f5]' : 'bg-white'}>
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 grid md:grid-cols-[1fr_1.4fr_auto] gap-6 md:gap-10 items-center">
            <div className="min-w-0">
              <h3 className="text-2xl font-bold text-[#1f2329] tracking-tight">{f.name}</h3>
              <p className="mt-2 text-xs text-[#8f959e] leading-relaxed">{f.summary}</p>
              <span className="mt-3 inline-flex text-[10px] font-semibold rounded-full bg-white border border-[#e6e8ee] px-2 py-0.5 text-[#646a73]">{f.plans}</span>
            </div>
            <div className="min-w-0">
              <ul className="space-y-2.5">
                {f.points.map((p) => (
                  <li key={p} className="flex gap-2 text-sm text-[#1f2329]">
                    <i className="ri-checkbox-circle-line text-[#2f51d6] text-lg leading-5 shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => onDemo(f.id)}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded bg-[#2f51d6] text-white text-xs font-bold uppercase tracking-wide hover:brightness-110 cursor-pointer"
                >
                  Live demo <i className="ri-play-circle-line text-sm" />
                </button>
                <Link
                  to={REGISTER}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded bg-white border border-[#e6e8ee] text-[#1f2329] text-xs font-bold uppercase tracking-wide hover:border-[#2f51d6]"
                >
                  Use this format <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
            <button type="button" onClick={() => onDemo(f.id)} className="cursor-pointer md:w-[300px]" aria-label={`Live demo: ${f.name}`}>
              <AdMockup id={f.id} />
            </button>
          </div>
        </div>
      ))}
    </section>
  );
}

function DashboardPreview() {
  const fmt = (n: number) => n.toLocaleString('en-US');
  return (
    <section className="bg-white py-16">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center">
          <h2 className="text-2xl md:text-[30px] font-bold text-[#1f2329] tracking-tight">Your dashboard, every view and click</h2>
          <p className="mt-2 text-sm text-[#646a73]">Manage campaigns, change ads and see results per format and partner app.</p>
        </div>
        <div className="mt-8 rounded-2xl border border-[#e6e8ee] bg-white overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <thead>
              <tr className="text-xs text-[#646a73] border-b border-[#f0f1f3]">
                <th className="px-5 py-3 font-medium">Campaign</th>
                <th className="px-4 py-3 font-medium">Format</th>
                <th className="px-4 py-3 font-medium">Partner app</th>
                <th className="px-4 py-3 font-medium text-right">Views</th>
                <th className="px-4 py-3 font-medium text-right">Clicks</th>
                <th className="px-4 py-3 font-medium text-right">Click rate</th>
                <th className="px-5 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {demoCampaigns.map((c) => (
                <tr key={c.name} className="border-t border-[#f5f6f7] text-sm">
                  <td className="px-5 py-3 font-semibold text-[#1f2329]">{c.name}</td>
                  <td className="px-4 py-3 text-[#41464f]">{c.format}</td>
                  <td className="px-4 py-3 text-[#41464f]">{c.app}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmt(c.views)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmt(c.clicks)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{((c.clicks / c.views) * 100).toFixed(1)}%</td>
                  <td className="px-5 py-3">
                    <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${c.status === 'Running' ? 'bg-[#e9faef] text-[#0e9f7e]' : 'bg-[#fff3e6] text-[#e5760b]'}`}>{c.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-5 py-2.5 text-[11px] text-[#a3a8b0] border-t border-[#f5f6f7]">Example data</p>
        </div>
      </div>
    </section>
  );
}

function Partners() {
  return (
    <section className="bg-[#f2f3f5] py-14">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <h2 className="text-center text-2xl md:text-[30px] font-bold text-[#1f2329] tracking-tight">Partner apps where your ads appear</h2>
        <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
          {partnerApps.map((a) => (
            <div key={a.id} className="rounded-2xl bg-white p-5 flex items-center gap-4">
              <span className="w-12 h-12 rounded-2xl flex items-center justify-center text-white text-2xl" style={{ background: a.color }}>
                <i className={a.icon} />
              </span>
              <div>
                <div className="font-bold text-[#1f2329]">{a.name}</div>
                <div className="text-sm text-[#646a73]">{a.tagline}</div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-center text-xs text-[#8f959e]">More partner apps join over time. Premium plans include every connected app.</p>
      </div>
    </section>
  );
}

export default function AdvertisePage() {
  const [demo, setDemo] = useState<AdFormatId | null>(null);
  useEffect(() => {
    document.title = 'Advertise | LoyaltyBridge';
  }, []);

  return (
    <>
      <Hero onDemo={() => setDemo('banner')} />
      <Steps />
      <Formats onDemo={setDemo} />
      <DashboardPreview />
      <Partners />
      <section className="bg-gradient-to-b from-[#f2f4ff] to-[#f4f2ff] py-16 text-center px-4">
        <h2 className="text-2xl md:text-[30px] font-bold text-[#1f2329] tracking-tight">Start advertising on partner apps</h2>
        <p className="mt-2 text-sm text-[#646a73]">Register, pick your plan and ad style, and go live after a quick review.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link to={REGISTER} className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#2f51d6] text-white text-sm font-semibold hover:brightness-110">
            Register as advertiser <i className="ri-arrow-right-line" />
          </Link>
          <Link to="/sponsor" className="inline-flex items-center h-11 px-6 rounded-full border border-[#2f51d6] text-[#2f51d6] bg-white text-sm font-semibold">
            See advertiser plans
          </Link>
        </div>
      </section>
      {demo && <LiveDemo key={demo} open initialFormat={demo} onClose={() => setDemo(null)} />}
    </>
  );
}
